// new code //
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import useToggleSidebar from "../../components/togglesidebar";
import Icons from "../../components/icons";
import SortIcon from "../../components/table/SortIcon";
import Sidebar from "../sidebar";
import Header from "../Header";
import Footer from "../footer";
import "../../styles/challengerequests.css";
import API_URL from "../../config/config";
import axios from "axios";
import Modal from "../../components/Modal/Modal";
import DateRangePickerComponent from "../../components/DateRangePicker";

let providersListCache = null;

function Providers() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(() => providersListCache?.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(() => providersListCache?.itemsPerPage || 10);
  const [searchTerm, setSearchTerm] = useState(() => providersListCache?.searchTerm || "");
  const [totalItems, setTotalItems] = useState(0);
  const token = localStorage.getItem("adminToken");
  const { isSidebarOpen, toggleSidebar } = useToggleSidebar();
  const [sortField, setSortField] = useState(() => providersListCache?.sortField || "createdAt");
  const [sortOrder, setSortOrder] = useState(() => providersListCache?.sortOrder || "desc");
  const [deleteId, setDeleteId] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [dateRange, setDateRange] = useState(
    () =>
      providersListCache?.dateRange || {
        startDate: null,
        endDate: null,
      }
  );
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const initialScrollYRef = useRef(providersListCache?.scrollY || 0);
  const scrollRestoredRef = useRef(false);

  // Bulk import state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importSuccess, setImportSuccess] = useState("");


  // Handle import file selection 
  const handleImportFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/csv",
      ];
      const isValid =
        validTypes.includes(file.type) || file.name.match(/\.(xlsx|xls|csv)$/);

      if (isValid) {
        setImportFile(file);
        setImportResults(null);
      } else {
        alert("Please select a valid Excel (.xlsx, .xls) or CSV file");
      }
    }
  };

  // Handle bulk import
  const handleBulkImport = async () => {
    if (!importFile) {
      alert("Please select a file to import");
      return;
    }

    setImporting(true);
    setImportResults(null);

    try {
      const formData = new FormData();
      formData.append("file", importFile);

      const response = await axios.post(
        `${API_URL.replace("/admin", "")}/providers/bulk-import`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setImportResults(response.data.results);
      setImportFile(null);

      if (response.data.results.success.length > 0) {
        setImportSuccess(
          `Successfully imported ${response.data.results.success.length} providers`
        );
        setTimeout(() => {
          setImportDialogOpen(false);
          setImportResults(null);
          setImportSuccess("");
          setRefreshTrigger((prev) => prev + 1);
        }, 3000);
      }
    } catch (err) {
      console.error("Import error:", err);
      setErrorMessage(err.response?.data?.message || "Failed to import file");
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setImporting(false);
    }
  };

  // Download sample template
  const downloadTemplate = () => {
    const csvContent = `Name,Cover Image,Icon Image,Location,Verified,Key Highlights,Features,Fabric,Category,Website Link,Facebook,Instagram,Pinterest,X,Youtube,Top Sellers,Rating
Example Provider,https://example.com/cover.jpg,https://example.com/icon.jpg,USA,Yes,"Modern fit, Anti-microbial","Antimicrobial, Wrinkle-resistant","Polyester, Spandex","Women, Men",https://example.com,https://facebook.com/example,https://instagram.com/example,https://pinterest.com/example,https://x.com/example,https://youtube.com/example,"Product A, Product B",4.5`;

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "provider-import-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        sortField,
        sortOrder,
      };

      if (dateRange.startDate) {
        params.startDate = dateRange.startDate.format("YYYY-MM-DD");
      }
      if (dateRange.endDate) {
        params.endDate = dateRange.endDate.format("YYYY-MM-DD");
      }

      const response = await axios.get(`${API_URL}/providers`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      if (response.data.success && response.data.items) {
        setData(response.data.items || []);
        setTotalItems(response.data.total || response.data.items.length);
      } else {
        setData([]);
        setTotalItems(0);
      }
    } catch (err) {
      console.error(
        "Error fetching providers list:",
        err.response?.data || err.message
      );
      setError(err.response?.data?.message || "Unable to fetch provider data.");
      setData([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [
    token,
    currentPage,
    itemsPerPage,
    searchTerm,
    sortField,
    sortOrder,
    dateRange,
  ]);

  useEffect(() => {
    providersListCache = {
      ...(providersListCache || {}),
      currentPage,
      itemsPerPage,
      searchTerm,
      sortField,
      sortOrder,
      dateRange,
    };
  }, [currentPage, itemsPerPage, searchTerm, sortField, sortOrder, dateRange]);

  useEffect(() => {
    return () => {
      providersListCache = {
        ...(providersListCache || {}),
        scrollY: window.scrollY || 0,
      };
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (scrollRestoredRef.current) return;
    scrollRestoredRef.current = true;
    const y = Number(initialScrollYRef.current || 0);
    if (y > 0) {
      requestAnimationFrame(() => window.scrollTo(0, y));
    }
  }, [loading]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [
    token,
    currentPage,
    itemsPerPage,
    searchTerm,
    sortField,
    sortOrder,
    dateRange,
    refreshTrigger,
    fetchData,
  ]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleDelete = async () => {
    if (!deleteId) {
      console.log("No deleteId set");
      return;
    }
    const idToDelete = deleteId;
    console.log("Deleting provider with ID:", idToDelete);
    try {
      const deleteUrl = `${API_URL}/providers/${idToDelete}`;
      console.log("Delete URL:", deleteUrl);
      const res = await axios.delete(deleteUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log("Delete success:", res.data);
      setSuccessMessage(
        "Provider soft deleted successfully. It will be hidden from admin panel but remain visible to users."
      );
      setErrorMessage("");
      setDeleteId(null); // Close modal after successful delete
      setRefreshTrigger((prev) => prev + 1);
      if (
        currentPage > 1 &&
        totalItems - 1 <= (currentPage - 1) * itemsPerPage &&
        (totalItems - 1) % itemsPerPage === 0
      ) {
        setCurrentPage((prev) => Math.max(1, prev - 1));
      }
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (err) {
      console.error("Delete failed:", err.response?.data || err.message);
      console.error("Full error:", err);
      setErrorMessage(
        err.response?.data?.message ||
        err.message ||
        "Failed to delete provider."
      );
      setSuccessMessage("");
      setDeleteId(null); // Close modal even on error
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  };

  const renderPaginationButtons = () => {
    const pages = [];
    const totalNumbersToShow = 5;
    const half = Math.floor(totalNumbersToShow / 2);

    let startPage = Math.max(currentPage - half, 1);
    let endPage = Math.min(currentPage + half, totalPages);

    if (currentPage <= half) {
      endPage = Math.min(totalNumbersToShow, totalPages);
    }

    if (currentPage > totalPages - half) {
      startPage = Math.max(totalPages - totalNumbersToShow + 1, 1);
    }

    if (totalPages < totalNumbersToShow) {
      startPage = 1;
      endPage = totalPages;
    }

    if (startPage > 1) {
      pages.push(
        <button
          key={1}
          className={`paginate_button ${currentPage === 1 ? "current active" : ""
            }`}
          onClick={() => setCurrentPage(1)}
        >
          1
        </button>
      );
      if (startPage > 2) {
        pages.push(
          <span key="start-ellipsis" className="ellipsis">
            ...
          </span>
        );
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          className={`paginate_button ${currentPage === i ? "current active" : ""
            }`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <span key="end-ellipsis" className="ellipsis">
            ...
          </span>
        );
      }
      pages.push(
        <button
          key={totalPages}
          className={`paginate_button ${currentPage === totalPages ? "current active" : ""
            }`}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    return pages;
  };

  const numTableColumns = 6;

  return (
    <>
      <Helmet>
        <title>Admin | Providers</title>
      </Helmet>
      <Sidebar isSidebarOpen={isSidebarOpen} />
      <div
        className={`content-overlay ${isSidebarOpen && window.innerWidth <= 992 ? "blurred" : ""
          }`}
        onClick={() =>
          isSidebarOpen && window.innerWidth <= 992 && toggleSidebar()
        }
      >
        <div
          className="content-wrapper"
          style={{
            marginLeft: isSidebarOpen ? "250px" : "70px",
            transition: "margin-left .3s",
          }}
        >
          <Header toggleSidebar={toggleSidebar} />
          <section className="content">
            <div className="breadcrumb-main-bx">
              <div className="breadcrumb-bx">
                <Link className="breadcrumb-link" to="/admin/dashboard">
                  Dashboard
                </Link>
                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                <div className="breadcrumb-link breadcrumb-active">
                  Providers
                </div>
              </div>
            </div>
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <div>Providers</div>
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          alignItems: "center",
                        }}
                      >
                        <DateRangePickerComponent
                          setDateRange={setDateRange}
                          initialDateRange={dateRange}
                        />
                        <Link
                          to="/admin/providers/add"
                          className="btn btn-success"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          ➕ Add Provider
                        </Link>
                        <button
                          className="btn btn-primary"
                          style={{ whiteSpace: "nowrap" }}
                          onClick={() => setImportDialogOpen(true)}
                        >
                          📊 Bulk Import
                        </button>
                      </div>
                    </div>
                    <div className="card-body">
                      {successMessage && (
                        <div
                          className="alert alert-success alert-dismissible fade show"
                          role="alert"
                          style={{
                            backgroundColor: "#d4edda",
                            color: "#155724",
                            border: "1px solid #c3e6cb",
                          }}
                        >
                          <strong>Success!</strong> {successMessage}
                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setSuccessMessage("")}
                          ></button>
                        </div>
                      )}
                      {importSuccess && (
                        <div
                          className="alert alert-success alert-dismissible fade show"
                          role="alert"
                          style={{
                            backgroundColor: "#d4edda",
                            color: "#155724",
                            border: "1px solid #c3e6cb",
                          }}
                        >
                          <strong>Success!</strong> {importSuccess}
                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setImportSuccess("")}
                          ></button>
                        </div>
                      )}
                      {errorMessage && (
                        <div
                          className="alert alert-danger alert-dismissible fade show"
                          role="alert"
                          style={{
                            backgroundColor: "#f8d7da",
                            color: "#721c24",
                            border: "1px solid #f5c6cb",
                          }}
                        >
                          <strong>Error!</strong> {errorMessage}
                          <button
                            type="button"
                            className="btn-close"
                            onClick={() => setErrorMessage("")}
                          ></button>
                        </div>
                      )}
                      <div
                        id="challenge-requests-table_wrapper"
                        className="dataTables_wrapper no-footer"
                      >
                        <div
                          className="dataTables_length"
                          id="challenge-requests-table_length"
                        >
                          <label>
                            Show
                            <select
                              value={itemsPerPage}
                              onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                              }}
                            >
                              <option value="10">10</option>
                              <option value="25">25</option>
                              <option value="50">50</option>
                              <option value="100">100</option>
                            </select>
                            entries
                          </label>
                        </div>
                        <div
                          id="challenge-requests-table_filter"
                          className="dataTables_filter"
                        >
                          <label>
                            Search:
                            <input
                              type="search"
                              className=""
                              placeholder="Search..."
                              aria-controls="challenge-requests-table"
                              value={searchTerm}
                              onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1);
                              }}
                            />
                          </label>
                        </div>
                        <div className="table-responsive">
                          <table
                            className="table table-bordered dataTable no-footer overflow-x-auto mt-2"
                            id="challenge-requests-table"
                          >
                            <thead>
                              <tr role="row">
                                <th>No.</th>
                                <th
                                  className={`sorting_${sortField === "name" ? sortOrder : ""
                                    }`}
                                  onClick={() => handleSort("name")}
                                >
                                  Name <SortIcon />
                                </th>
                                <th
                                  className={`sorting_${sortField === "location" ? sortOrder : ""
                                    }`}
                                  onClick={() => handleSort("location")}
                                >
                                  URL <SortIcon />
                                </th>
                                <th
                                  className={`sorting_${sortField === "rating" ? sortOrder : ""
                                    }`}
                                  onClick={() => handleSort("rating")}
                                >
                                  Rating <SortIcon />
                                </th>
                                <th
                                  className={`sorting_${sortField === "createdAt" ? sortOrder : ""
                                    }`}
                                  onClick={() => handleSort("createdAt")}
                                >
                                  Created At <SortIcon />
                                </th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                Array.from({ length: itemsPerPage }).map(
                                  (_, rowIndex) => (
                                    <tr key={`skeleton-${rowIndex}`}>
                                      <td>
                                        <div className="skeletant-bx skeletant-design sh-47"></div>
                                      </td>
                                      <td>
                                        <div className="skeletant-bx skeletant-design sh-47"></div>
                                      </td>
                                      <td>
                                        <div className="skeletant-bx skeletant-design sh-47"></div>
                                      </td>
                                      <td>
                                        <div className="skeletant-bx skeletant-design sh-47"></div>
                                      </td>
                                      <td>
                                        <div className="skeletant-bx skeletant-design sh-47"></div>
                                      </td>
                                      <td>
                                        <div className="skeletant-bx skeletant-design sh-47"></div>
                                      </td>
                                    </tr>
                                  )
                                )
                              ) : error ? (
                                <tr>
                                  <td
                                    colSpan={numTableColumns}
                                    style={{
                                      textAlign: "center",
                                      padding: "10px",
                                      color: "red",
                                    }}
                                  >
                                    {error}
                                  </td>
                                </tr>
                              ) : data.length > 0 ? (
                                data.map((row, index) => (
                                  <tr key={row._id || row.id}>
                                    <td>
                                      {itemsPerPage * (currentPage - 1) +
                                        index +
                                        1}
                                    </td>
                                    <td>{row.name || "N/A"}</td>
                                    <td>{row.url || "N/A"}</td>
                                    <td>{row.rating || 0}</td>
                                    <td>
                                      {row.createdAt
                                        ? new Date(
                                          row.createdAt
                                        ).toLocaleDateString()
                                        : "N/A"}
                                    </td>
                                    <td>
                                      <Link
                                        to={`/admin/providers/${row._id || row.id
                                          }`}
                                        className="common-action-btn"
                                        title="View"
                                      >
                                        <Icons.ViewIcon />
                                      </Link>
                                      <Link
                                        to={`/admin/providers/edit/${row._id || row.id
                                          }`}
                                        className="common-action-btn toggle-status"
                                        title="Edit"
                                      >
                                        <Icons.EditIcon />
                                      </Link>
                                      <Link
                                        to="#"
                                        className="common-action-btn"
                                        title="delete"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          setDeleteId(row._id || row.id);
                                        }}
                                      >
                                        <Icons.DeleteIcon />
                                      </Link>

                                      <Link
                                        to={`/admin/providers/products/${row._id || row.id
                                          }`}
                                        className="common-action-btn"
                                        title="Products"
                                      >
                                        <Icons.ProductBagIcon />
                                      </Link>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td
                                    colSpan={numTableColumns}
                                    style={{
                                      textAlign: "center",
                                      padding: "10px",
                                    }}
                                  >
                                    No data available in table
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        <div className="dataTables_info">
                          Showing{" "}
                          {totalItems === 0
                            ? 0
                            : itemsPerPage * (currentPage - 1) + 1}{" "}
                          to {Math.min(itemsPerPage * currentPage, totalItems)}{" "}
                          of {totalItems} entries
                        </div>

                        <div className="dataTables_paginate paging_simple_numbers">
                          <Link
                            to="#"
                            className={`paginate_button previous ${currentPage === 1 ? "disabled" : ""
                              }`}
                            onClick={() =>
                              setCurrentPage((prev) => Math.max(prev - 1, 1))
                            }
                          >
                            Previous
                          </Link>
                          <span>{renderPaginationButtons()}</span>
                          <Link
                            to="#"
                            className={`paginate_button next ${currentPage === totalPages ? "disabled" : ""
                              }`}
                            onClick={() =>
                              setCurrentPage((prev) =>
                                Math.min(prev + 1, totalPages)
                              )
                            }
                          >
                            Next
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <Footer />
        </div>
      </div>
      <Modal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Provider"
        message="Are you sure you want to soft delete this provider? It will be hidden from the admin panel but remain visible to users."
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Bulk Import Modal */}
      {importDialogOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "30px",
              maxWidth: "500px",
              width: "90%",
              maxHeight: "80vh",
              overflowY: "auto",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "600" }}>
                Bulk Import Providers
              </h2>
              <button
                onClick={() => setImportDialogOpen(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>

            <p style={{ color: "#666", marginBottom: "20px" }}>
              Upload an Excel (.xlsx, .xls) or CSV file to import multiple
              providers at once.
            </p>

            <div
              style={{
                border: "2px dashed #ddd",
                borderRadius: "8px",
                padding: "15px",
                backgroundColor: "#f9f9f9",
                marginBottom: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                }}
              >
                <span style={{ fontSize: "20px", marginTop: "4px" }}>📥</span>
                <div style={{ flex: 1 }}>
                  <h4
                    style={{
                      margin: "0 0 8px 0",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                  >
                    Download Template
                  </h4>
                  <p
                    style={{
                      margin: "0 0 10px 0",
                      fontSize: "12px",
                      color: "#666",
                    }}
                  >
                    Start by downloading our template file with the correct
                    column headers and format.
                  </p>
                  <button
                    onClick={downloadTemplate}
                    style={{
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #ddd",
                      padding: "8px 16px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "500",
                    }}
                  >
                    📄 Download CSV Template
                  </button>
                </div>
              </div>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "8px",
                }}
              >
                Upload File
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleImportFileChange}
                  style={{
                    flex: 1,
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "12px",
                  }}
                />
                <button
                  onClick={handleBulkImport}
                  disabled={!importFile || importing}
                  style={{
                    backgroundColor:
                      importFile && !importing ? "#28a745" : "#ccc",
                    color: "white",
                    border: "none",
                    padding: "8px 20px",
                    borderRadius: "4px",
                    cursor:
                      importFile && !importing ? "pointer" : "not-allowed",
                    fontSize: "12px",
                    fontWeight: "500",
                  }}
                >
                  {importing ? "Importing..." : "Import"}
                </button>
              </div>
              {importFile && (
                <p
                  style={{ fontSize: "11px", color: "#666", marginTop: "6px" }}
                >
                  Selected: {importFile.name}
                </p>
              )}
            </div>

            {/* Results */}
            {importResults && (
              <div style={{ marginTop: "20px" }}>
                <div
                  style={{
                    border: "1px solid #d4edda",
                    backgroundColor: "#d4edda",
                    borderRadius: "4px",
                    padding: "12px",
                    marginBottom: "12px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginBottom: "6px",
                    }}
                  >
                    <span style={{ fontSize: "16px" }}>✓</span>
                    <h4
                      style={{
                        margin: 0,
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#155724",
                      }}
                    >
                      Import Complete
                    </h4>
                  </div>
                  <p style={{ margin: 0, fontSize: "12px", color: "#155724" }}>
                    Successfully imported {importResults.success.length} out of{" "}
                    {importResults.total} providers.
                  </p>
                </div>

                {importResults.errors.length > 0 && (
                  <div
                    style={{
                      border: "1px solid #f5c6cb",
                      backgroundColor: "#f8d7da",
                      borderRadius: "4px",
                      padding: "12px",
                      maxHeight: "200px",
                      overflowY: "auto",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <span style={{ fontSize: "16px" }}>⚠️</span>
                      <h4
                        style={{
                          margin: 0,
                          fontSize: "13px",
                          fontWeight: "600",
                          color: "#721c24",
                        }}
                      >
                        Errors ({importResults.errors.length})
                      </h4>
                    </div>
                    <div>
                      {importResults.errors.map((error, idx) => (
                        <p
                          key={idx}
                          style={{
                            margin: "4px 0",
                            fontSize: "11px",
                            color: "#721c24",
                          }}
                        >
                          Row {error.row} ({error.name}): {error.error}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}


export default Providers;
